"use client";

/**
 * Client-side demo seeder for pilot.
 * Creates: 1 program, 3 courses, 1 verification — all linked to current user.
 *
 * In production: replace with Admin SDK script + service account.
 */

import { doc, setDoc, collection, getDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { getFirebase } from "./client";

export async function seedDemoData(args: {
  uid: string;
  displayName: string;
  email: string;
}): Promise<{ created: number; skipped: number }> {
  const { db } = getFirebase();
  let created = 0;
  let skipped = 0;

  // 1. Program — ETC (Educational Technology and Computer Education)
  // OVERWRITES the program doc to guarantee correct name (replaces any
  // older "Computer Science" demo content that may have been seeded earlier).
  const programRef = doc(db, "programs", "demo-cs");
  const programSnap = await getDoc(programRef);
  await setDoc(programRef, {
    code: "ETC-BEd",
    nameTh: "เทคโนโลยีการศึกษาและคอมพิวเตอร์ศึกษา",
    nameEn: "Educational Technology and Computer Education",
    degree: "กศ.บ.",
    degreeNameTh: "การศึกษาบัณฑิต",
    department: "ภาควิชาเทคโนโลยีและสื่อสารการศึกษา",
    faculty: "คณะศึกษาศาสตร์",
    facultyEn: "Faculty of Education",
    university: "มหาวิทยาลัยมหาสารคาม",
    universityEn: "Mahasarakham University",
    level: "bachelor",
    curriculumYear: 2567,
    phone: "0-4372-1764 ต่อ 6235",
    docNoPrefix: "อว 0605.5(6)/",
    chairName: "ผู้ช่วยศาสตราจารย์ ดร.มานิตย์ อาษานอก",
    chairTitle: "ประธานหลักสูตร",
    deptHeadName: "ผู้ช่วยศาสตราจารย์ ดร.ธนดล ภูสีฤทธิ์",
    deptHeadTitle: "หัวหน้าภาควิชา",
    systemUrl: "https://verification-msu.web.app/",
    primaryColor: "#dc2626",
    emoji: "💻",
    active: true,
    createdAt: programSnap.exists() ? (programSnap.data()?.createdAt ?? serverTimestamp()) : serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  if (!programSnap.exists()) created++;
  else skipped++;

  // 2. Sample courses — only created if they don't exist (preserve existing real courses)
  // Note: real ETC courses are loaded via Bulk Assign from INSTRUCTORS_2568, not from this seed
  const courses: Array<{ id: string; code: string; nameTh: string; nameEn: string; credit: number; type: string; lastVerifiedYear: number }> = [];

  for (const c of courses) {
    const ref = doc(db, "programs", "demo-cs", "courses", c.id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        programId: "demo-cs",
        code: c.code,
        nameTh: c.nameTh,
        nameEn: c.nameEn,
        credit: c.credit,
        type: c.type,
        instructorUid: args.uid,
        instructorName: args.displayName,
        lastVerifiedYear: c.lastVerifiedYear,
        verificationCount: 0,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      created++;
    } else {
      skipped++;
    }
  }

  // 3. Demo verification (committee = current user)
  const verifyRef = doc(db, "programs", "demo-cs", "verifications", "demo-verify-1");
  const verifySnap = await getDoc(verifyRef);
  if (!verifySnap.exists()) {
    await setDoc(verifyRef, {
      programId: "demo-cs",
      courseId: "cs101",
      semesterId: "2568-1",
      courseCode: "0123101",
      courseName: "หลักการเขียนโปรแกรม",
      status: "in_progress",
      scheduledDate: Timestamp.now(),
      committee: [
        { uid: args.uid, name: args.displayName, role: "chair" },
      ],
      signatures: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    created++;
  } else {
    skipped++;
  }

  return { created, skipped };
}
